import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { BRAND } from "@/lib/brand";

const Section = ({ n, title, children }) => (
  <section className="border-t border-white/10 py-10">
    <div className="grid md:grid-cols-12 gap-6">
      <div className="md:col-span-3">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold">
          Chapter {n.toString().padStart(2, "0")}
        </div>
        <h2 className="font-display font-bold text-white text-xl md:text-2xl mt-2 tracking-tight leading-tight">
          {title}
        </h2>
      </div>
      <div className="md:col-span-9 space-y-4 text-zinc-300 text-[15px] leading-relaxed">
        {children}
      </div>
    </div>
  </section>
);

const P = ({ children }) => <p>{children}</p>;
const UL = ({ items }) => (
  <ul className="space-y-2 pl-1">
    {items.map((t, i) => (
      <li key={i} className="flex items-start gap-3">
        <span className="text-[#E60000] mt-1">·</span>
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

export default function Privacy() {
  return (
    <main data-testid="privacy-page" className="bg-[#050505] text-white">
      <Header />
      <div className="pt-28">
        <div className="max-w-5xl mx-auto px-6 md:px-10 pb-20">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold">
            MHJ Prestige Transport Ltd
          </div>
          <h1 className="font-display font-extrabold text-white text-4xl md:text-6xl leading-[1.0] tracking-tight mt-4">
            Privacy Policy
          </h1>
          <p className="text-zinc-400 text-sm mt-4 max-w-2xl">
            How we handle your personal information — in plain English.
          </p>

          <Section n={1} title="What we collect">
            <P>To quote and deliver transport we collect:</P>
            <UL items={[
              "Contact details — name, phone, email, preferred contact method",
              "Booking details — vehicle registration, make, model, condition, pickup and delivery addresses",
              "Payment details — processed directly by Stripe (we never store card numbers)",
              "Communication — messages you send us by form, email, WhatsApp or phone",
              "Basic analytics — cookies/local storage strictly needed to run the site",
            ]}/>
          </Section>

          <Section n={2} title="Why we collect it">
            <UL items={[
              "To produce accurate quotations",
              "To process and administer your booking",
              "To arrange vehicle transport (including with subcontracted drivers where required)",
              "To take payment and refunds",
              "To contact you about your booking or reply to your enquiries",
              "To meet our legal obligations (accounting, insurance, complaints)",
            ]}/>
          </Section>

          <Section n={3} title="Who we share it with">
            <P>Only the parties needed to deliver your job:</P>
            <UL items={[
              "Our own drivers and, where required, appropriately selected subcontracted transporters",
              "Stripe (payment processor) — see stripe.com/privacy",
              "Google Maps Platform — used only to compute driving distances",
              "Resend (email delivery) — used to send booking notifications",
              "Auction / storage facilities where you are collecting from them",
              "HMRC and other authorities where legally required",
            ]}/>
            <P>We do not sell your data or share it with advertisers.</P>
          </Section>

          <Section n={4} title="How long we keep it">
            <P>
              Booking, payment and accounting records are retained for as long as UK tax and consumer
              law requires (typically 6 years). Enquiries and quote data we don&apos;t need are removed
              on request or after 24 months of inactivity.
            </P>
          </Section>

          <Section n={5} title="Your rights">
            <P>Under UK GDPR you have the right to:</P>
            <UL items={[
              "Access the personal data we hold about you",
              "Ask us to correct anything inaccurate",
              "Ask us to delete data where we no longer need it (subject to legal retention)",
              "Object to processing based on our legitimate interests",
              "Complain to the ICO (ico.org.uk) if you are unhappy with how we handle your data",
            ]}/>
          </Section>

          <Section n={6} title="Marketing">
            <P>We do not send marketing emails unless you have separately opted in via the booking form or a similar clearly-labelled tick-box. Marketing consent is never pre-ticked and is always separate from your acceptance of the Terms &amp; Conditions.</P>
          </Section>

          <Section n={7} title="Contact us">
            <P>
              For anything relating to your personal data, email{" "}
              <a className="underline" href={`mailto:${BRAND.email}`}>{BRAND.email}</a> or call{" "}
              <a className="underline" href={`tel:${BRAND.phoneRaw}`}>{BRAND.phone}</a>.
            </P>
          </Section>

          <div className="mt-16 pt-8 border-t border-white/10 text-xs text-zinc-500">
            Version 1.0 · Last updated {new Date().toLocaleDateString("en-GB")}
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}
